import { useEffect, useRef } from 'react';

import type { ChatMessage, ChatSenderType } from '#shared/types/chat';
import { ChatBubble } from './ChatBubble';
import { TypingDots } from './TypingDots';

export interface ChatMessageListProps {
  messages: ChatMessage[];
  /** Welke kant is "ik". De bezoeker ziet 'customer' rechts, de medewerker 'agent'. */
  ownSide: ChatSenderType;
  typingLabel?: string | null;
  /** Foto van de medewerker die dit gesprek voert. Eén gesprek heeft één
   *  medewerker, dus dit hoeft niet per bericht mee. */
  agentAvatar?: string | null;
}

export function ChatMessageList({ messages, ownSide, typingLabel, agentAvatar }: ChatMessageListProps) {
  const endRef = useRef<HTMLDivElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);

  // Meescrollen — maar alleen als de lezer al onderaan zat. Iemand die
  // terugbladert door het gesprek mag niet worden weggetrokken door een nieuw
  // bericht.
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const nearBottom = box.scrollHeight - box.scrollTop - box.clientHeight < 120;
    if (nearBottom) endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, typingLabel]);

  return (
    <div
      ref={boxRef}
      // tabIndex={0} is VERPLICHT: een scrollbare div zonder focusbare kinderen
      // is niet met het toetsenbord te scrollen, en axe's
      // scrollable-region-focusable zit in de wcag2a-set die verify-a11y.mjs:59
      // draait. Dit is de meest waarschijnlijke nieuwe overtreding van de hele
      // functie.
      tabIndex={0}
      // role="log" is de juiste rol voor een transcript, maar aria-live UIT:
      // anders wordt élk bericht dubbel omgeroepen (één keer hier, één keer door
      // de omroeper in ChatLauncher) én hoor je je eigen echo terug.
      role="log"
      aria-live="off"
      aria-label="Gespreksverloop"
      className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto bg-canvas p-3"
    >
      {messages.map((message, i) => {
        const previous = messages[i - 1];
        return (
          <ChatBubble
            key={message.id}
            message={message}
            own={message.senderType === ownSide}
            showName={previous?.senderName !== message.senderName}
            avatarUrl={message.senderType === 'agent' ? agentAvatar : null}
          />
        );
      })}
      {typingLabel && <TypingDots label={typingLabel} />}
      <div ref={endRef} />
    </div>
  );
}
